import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useStore } from "./StoreLayout";
import api, { money, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, Package, Minus, Plus, Lock } from "lucide-react";

export default function StoreProduct() {
  const { slug, pid } = useParams();
  const navigate = useNavigate();
  const { addToCart, isCustomer, store } = useStore();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [showPrice, setShowPrice] = useState(true);

  useEffect(() => {
    api.get(`/public/stores/${slug}/products/${pid}`).then((r) => {
      setP(r.data);
      setShowPrice(r.data.price !== undefined);
    }).catch(() => navigate(`/store/${slug}`));
  }, [slug, pid, navigate]);

  if (!p) return <FullScreenLoader />;

  const canOrder = (isCustomer || store.visitor_settings?.allow_orders) && p.in_stock && showPrice;

  const add = () => { addToCart(p, qty); toast.success(`${p.name} added to cart`); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Link to={`/store/${slug}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-store">
        <ArrowLeft className="h-4 w-4" /> Back to store
      </Link>
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square rounded-xl bg-secondary overflow-hidden">
            {p.images?.[img] ? <img src={fileUrl(p.images[img])} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
              : <div className="w-full h-full flex items-center justify-center"><Package className="h-16 w-16 text-muted-foreground/40" /></div>}
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setImg(i)} className={`h-16 w-16 rounded-lg overflow-hidden border-2 ${img === i ? "border-primary" : "border-transparent"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{p.name}</h1>
          {p.sku && <p className="text-sm text-muted-foreground mt-1">SKU: {p.sku}</p>}
          <div className="mt-4">
            {showPrice ? (
              <p className="text-3xl font-bold">{money(p.price)}</p>
            ) : (
              <Link to={`/store/${slug}/login`} className="inline-flex items-center gap-2 text-primary font-medium"><Lock className="h-4 w-4" /> Sign in to view price</Link>
            )}
            <p className={`text-sm mt-1 ${p.in_stock ? "text-emerald-600" : "text-destructive"}`}>{p.in_stock ? `In stock (${p.quantity})` : "Out of stock"}</p>
          </div>
          {p.description && <p className="mt-5 text-muted-foreground leading-relaxed whitespace-pre-wrap">{p.description}</p>}
          {p.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {p.tags.map((t) => <span key={t} className="bg-secondary rounded-full px-2.5 py-1 text-xs font-medium">{t}</span>)}
            </div>
          )}
          {canOrder && (
            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center border border-border rounded-lg">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 hover:bg-secondary" data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
                <span className="w-10 text-center font-medium" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-2.5 hover:bg-secondary" data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
              </div>
              <Button onClick={add} className="rounded-full flex-1 h-11" data-testid="product-add-to-cart">Add to cart</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
